import { useState } from 'react';

import { MessageSquare, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { useGtFileStatus, useSaveGtFileRemark } from './api';
import { getServerErrorMessage } from './errors';
import { QUIET_TOAST } from './hubToast';

/** The reviewer's remark on the whole file: add, read or edit it; it saves on its own, apart from the documents. */
export function FileRemarkBox({
    ingestedFileId,
    compact = false,
}: {
    ingestedFileId: number;
    /** Empty state is a helper line and a text link instead of an outlined button. */
    compact?: boolean;
}) {
    const { data: status } = useGtFileStatus(ingestedFileId);
    const save = useSaveGtFileRemark(ingestedFileId);
    // null while the box shows the saved remark; a string while the person edits.
    const [draft, setDraft] = useState<string | null>(null);

    // Wait for the saved remark, or "Add" would hide a remark that is already there.
    if (!status) return null;
    const remark = status.fileRemark ?? '';

    const submit = () => {
        save.mutate((draft ?? '').trim(), {
            onSuccess: () => {
                setDraft(null);
                toast.success('File remark saved', QUIET_TOAST);
            },
            onError: (error) =>
                toast.error(getServerErrorMessage(error, 'the file remark')),
        });
    };

    if (draft !== null) {
        return (
            <div
                className="flex flex-col gap-2 rounded-lg border border-[#CBE1E1] bg-white p-3"
                data-testid="FileRemarkBox__editor"
            >
                <label
                    htmlFor="gt-file-remark"
                    className="text-xs font-medium text-slate-900"
                >
                    File remark{' '}
                    <span className="font-normal text-slate-500">
                        (about the whole file: scan quality, missing pages, open
                        questions)
                    </span>
                </label>
                <Textarea
                    id="gt-file-remark"
                    autoFocus
                    rows={3}
                    value={draft}
                    placeholder="What should the next reviewer know about this file?"
                    className="px-2 py-2 text-sm"
                    onChange={(e) => setDraft(e.target.value)}
                    data-testid="FileRemarkBox__textarea"
                />
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        size="sm"
                        className="h-8 bg-[#32ACAC] text-xs text-white hover:bg-[#2A9696]"
                        disabled={save.isPending}
                        onClick={submit}
                        data-testid="FileRemarkBox__save"
                    >
                        Save remark
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-slate-600"
                        disabled={save.isPending}
                        onClick={() => setDraft(null)}
                        data-testid="FileRemarkBox__cancel"
                    >
                        Cancel
                    </Button>
                    <span className="text-xs text-slate-600">
                        Saves straight away, apart from the documents. Empty it to
                        remove the remark.
                    </span>
                </div>
            </div>
        );
    }

    if (remark !== '') {
        return (
            <button
                type="button"
                aria-label="Edit file remark"
                className="flex w-full items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-[13px] leading-[18px] text-amber-900"
                onClick={() => setDraft(remark)}
                data-testid="FileRemarkBox__filled"
            >
                <MessageSquare
                    aria-hidden="true"
                    className="mt-px h-4 w-4 shrink-0 text-amber-700"
                />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-xs font-semibold text-amber-800">
                        File remark
                    </span>
                    <span className="whitespace-pre-wrap break-words">
                        {remark}
                    </span>
                </span>
                <span className="text-xs text-amber-800 underline">Edit</span>
            </button>
        );
    }

    if (compact) {
        return (
            <div className="flex flex-col items-start gap-2">
                <p className="text-xs text-slate-600">
                    Anything wrong with the file itself? Missing pages, bad scan,
                    open questions.
                </p>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#2F7F7F] hover:underline"
                    onClick={() => setDraft('')}
                    data-testid="FileRemarkBox__add"
                >
                    <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                    Add file remark
                </button>
            </div>
        );
    }

    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 self-start border-dashed text-xs text-slate-600"
            onClick={() => setDraft('')}
            data-testid="FileRemarkBox__add"
        >
            <Plus aria-hidden="true" className="mr-2 h-3.5 w-3.5" />
            Add file remark
        </Button>
    );
}
